/**
 * Notes: what the team knows about this client that the record does not say,
 * as a timeline of signed, dated entries. Everyone with access to the client
 * reads, adds and pins them; only an entry's author edits or deletes it. The
 * assistant reads them before every turn about the client (pinned first, then
 * newest) and adds to them only through a suggestion a person approves, here
 * or in its chat.
 */
import { Pin, Search, StickyNote, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC
} from "react";
import { toast } from "sonner";

import {
  ForeignNationalsAPI,
  type ForeignNationalNote,
  type ForeignNationalNoteProposal
} from "@/api/foreignNationals";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { FilterChip } from "@/components/ui/filter-chip";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput
} from "@/components/ui/input-group";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetUserDetails } from "@/hooks/useGetUserDetails";
import { useUserProfiles } from "@/features/userProfiles/context/UserProfilesContext";

import type { MatterDTO } from "../../../../api/types/matter";
import { SectionHeader } from "../../components/SectionHeader";
import { useClientProfile } from "../../context/ClientProfileContext";
import { NoteComposer, type CaseOption, type NoteDraft } from "./NoteComposer";
import { NoteEntry } from "./NoteEntry";
import { NoteProposalCard } from "./NoteProposalCard";
import {
  ASSISTANT_KEY,
  EVERYONE,
  authorChips,
  filterNotes,
  groupNotes,
  isNarrowed,
  type NoteFilters
} from "./notesModel";

const DEFAULT_MAX_CHARS = 20_000;
/** A deleted entry is removed for real once its Undo toast is gone. */
const UNDO_MS = 5_000;
/** After approving a suggestion the thread writes the entry on its own; look again. */
const REFRESH_AFTER_APPROVAL_MS = [1_500, 5_000];

const str = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

export const caseOptionOf = (m: MatterDTO): CaseOption => {
  const visa =
    str(m.case?.visa) ||
    str((m.individual as Record<string, unknown> | undefined)?.visa);
  return { id: m.individual_id, label: visa ? `${visa} Case` : "Case" };
};

const GroupLabel: FC<{ icon?: boolean; label: string; count: number }> = ({
  icon,
  label,
  count
}) => (
  <div className="flex h-7 items-center gap-1.5 px-2.5 text-sm font-medium text-fg-3">
    {icon ? <Pin className="size-3" /> : null}
    {label}
    <span className="font-normal text-fg-4 tabular-nums">{count}</span>
  </div>
);

export const NotesTimeline: FC = () => {
  const { clientId, matters, url } = useClientProfile();
  const {
    userDetails: { id: me, userFullName, avatarSrc }
  } = useGetUserDetails();
  const profiles = useUserProfiles();

  const [notes, setNotes] = useState<ForeignNationalNote[]>([]);
  const [proposals, setProposals] = useState<ForeignNationalNoteProposal[]>([]);
  const [maxChars, setMaxChars] = useState(DEFAULT_MAX_CHARS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<NoteFilters>({
    query: "",
    author: EVERYONE,
    pinnedOnly: false
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [answering, setAnswering] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const pendingDeletes = useRef(
    new Map<string, ReturnType<typeof setTimeout>>()
  );

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        const [list, pending] = await Promise.all([
          ForeignNationalsAPI.listNotes(clientId),
          ForeignNationalsAPI.listNoteProposals(clientId).catch(() => [])
        ]);
        setNotes(list.notes.filter((n) => !pendingDeletes.current.has(n.id)));
        setMaxChars(list.max_chars || DEFAULT_MAX_CHARS);
        setProposals(pending);
        setError(null);
      } catch (err) {
        if (!quiet)
          setError(
            err instanceof Error ? err.message : "Could not load the notes."
          );
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [clientId]
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Deletes still waiting on their Undo window go through when the tab closes.
  useEffect(() => {
    const pending = pendingDeletes.current;
    return () => {
      for (const [noteId, timer] of pending) {
        clearTimeout(timer);
        void ForeignNationalsAPI.deleteNote(clientId, noteId).catch(
          () => undefined
        );
      }
      pending.clear();
    };
  }, [clientId]);

  const { ensure } = profiles;
  useEffect(() => {
    const ids = [
      ...new Set(notes.map((n) => n.author_uid).filter(Boolean))
    ] as string[];
    if (ids.length) ensure(ids).catch(() => undefined);
  }, [notes, ensure]);

  const focusNote = url.state.focus.note;
  useEffect(() => {
    if (!focusNote || loading) return undefined;
    const el = document.getElementById(`note-${focusNote}`);
    if (!el) return undefined;
    el.scrollIntoView?.({ block: "center", behavior: "smooth" });
    setHighlightId(focusNote);
    const timer = setTimeout(() => setHighlightId(null), 2_000);
    return () => clearTimeout(timer);
  }, [focusNote, loading]);

  const cases = useMemo(
    () => matters.matters.map(caseOptionOf),
    [matters.matters]
  );
  const caseLabelOf = useCallback(
    (caseId: string | null) =>
      (caseId && cases.find((c) => c.id === caseId)?.label) || null,
    [cases]
  );

  const chips = useMemo(() => authorChips(notes), [notes]);
  const filtered = useMemo(
    () => filterNotes(notes, filters, caseLabelOf),
    [notes, filters, caseLabelOf]
  );
  const { pinned, months } = useMemo(() => groupNotes(filtered), [filtered]);
  const narrowed = isNarrowed(filters);
  const pinnedCount = notes.filter((n) => n.pinned).length;
  const showProposals =
    proposals.length > 0 &&
    !filters.query.trim() &&
    !filters.pinnedOnly &&
    (filters.author === EVERYONE || filters.author === ASSISTANT_KEY);
  const replace = (note: ForeignNationalNote) =>
    setNotes((all) => all.map((n) => (n.id === note.id ? note : n)));

  const create = async (draft: NoteDraft): Promise<boolean> => {
    try {
      const note = await ForeignNationalsAPI.createNote(clientId, {
        body: draft.body,
        pinned: draft.pinned,
        case_id: draft.caseId
      });
      setNotes((all) => [note, ...all]);
      toast.success("Note added. The assistant reads it from the next turn.");
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not add the note."
      );
      return false;
    }
  };

  const saveEdit = async (note: ForeignNationalNote, body: string) => {
    try {
      replace(
        await ForeignNationalsAPI.updateNote(clientId, note.id, { body })
      );
      setEditingId(null);
      toast.success("Note saved");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not save the note."
      );
    }
  };

  const togglePin = async (note: ForeignNationalNote) => {
    const next = !note.pinned;
    replace({ ...note, pinned: next });
    try {
      replace(
        await ForeignNationalsAPI.updateNote(clientId, note.id, {
          pinned: next
        })
      );
      toast.success(
        next
          ? "Pinned. Pinned notes stay on top and the assistant reads them first."
          : "Unpinned"
      );
    } catch (err) {
      replace(note);
      toast.error(
        err instanceof Error ? err.message : "Could not pin the note."
      );
    }
  };

  const copyLink = async (note: ForeignNationalNote) => {
    const link = `${window.location.origin}/clients/${clientId}?tab=notes&note=${note.id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  const remove = (note: ForeignNationalNote) => {
    setNotes((all) => all.filter((n) => n.id !== note.id));
    const timer = setTimeout(() => {
      pendingDeletes.current.delete(note.id);
      ForeignNationalsAPI.deleteNote(clientId, note.id).catch((err) => {
        setNotes((all) => [note, ...all]);
        toast.error(
          err instanceof Error ? err.message : "Could not delete the note."
        );
      });
    }, UNDO_MS);
    pendingDeletes.current.set(note.id, timer);
    toast("Note deleted", {
      duration: UNDO_MS,
      action: {
        label: "Undo",
        onClick: () => {
          clearTimeout(timer);
          pendingDeletes.current.delete(note.id);
          setNotes((all) => [note, ...all]);
        }
      }
    });
  };

  const answer = async (
    proposal: ForeignNationalNoteProposal,
    approved: boolean
  ) => {
    setAnswering(proposal.approval_id);
    try {
      await ForeignNationalsAPI.answerNoteProposal(
        clientId,
        proposal,
        approved
      );
      setProposals((all) =>
        all.filter((p) => p.approval_id !== proposal.approval_id)
      );
      if (approved) {
        toast.success("Note added. The assistant reads it from the next turn.");
        for (const ms of REFRESH_AFTER_APPROVAL_MS)
          setTimeout(() => void load(true), ms);
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not answer the suggestion."
      );
      void load(true);
    } finally {
      setAnswering(null);
    }
  };

  const clearFilters = () =>
    setFilters({ query: "", author: EVERYONE, pinnedOnly: false });

  const narrowedLabel = () => {
    const parts = [
      `${filtered.length} result${filtered.length === 1 ? "" : "s"}`
    ];
    if (filters.query.trim()) parts.push(`for "${filters.query.trim()}"`);
    if (filters.author !== EVERYONE) {
      const chip = chips.find((c) => c.key === filters.author);
      if (chip) parts.push(`by ${chip.label}`);
    }
    return parts.join(" ") + (filters.pinnedOnly ? " · pinned" : "");
  };

  const entry = (note: ForeignNationalNote) => (
    <NoteEntry
      key={note.id}
      note={note}
      query={filters.query}
      own={Boolean(me) && note.author_uid === me}
      avatar={
        note.author_uid
          ? profiles.get(note.author_uid)?.avatar_data_url ?? undefined
          : undefined
      }
      caseLabel={caseLabelOf(note.case_id)}
      highlighted={highlightId === note.id}
      editing={editingId === note.id}
      maxChars={maxChars}
      onEdit={() => setEditingId(note.id)}
      onCancel={() => setEditingId(null)}
      onSave={(body) => saveEdit(note, body)}
      onTogglePin={() => void togglePin(note)}
      onCopyLink={() => void copyLink(note)}
      onDelete={() => remove(note)}
    />
  );

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col"
      data-testid="client-notes"
    >
      <SectionHeader
        title="Notes"
        description={loading ? undefined : String(notes.length)}
        actions={
          <InputGroup className="h-7 w-56 rounded-md">
            <InputGroupAddon>
              <Search className="size-3.5" />
            </InputGroupAddon>
            <InputGroupInput
              aria-label="Search Notes"
              placeholder="Search notes"
              value={filters.query}
              onChange={(e) =>
                setFilters((f) => ({ ...f, query: e.target.value }))
              }
            />
            {filters.query ? (
              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  size="icon-xs"
                  aria-label="Clear Search"
                  onClick={() => setFilters((f) => ({ ...f, query: "" }))}
                >
                  <X />
                </InputGroupButton>
              </InputGroupAddon>
            ) : null}
          </InputGroup>
        }
      />
      {notes.length > 0 ? (
        <div className="flex h-11 shrink-0 items-center gap-1.5 overflow-x-auto px-4">
          {chips.map((chip) => (
            <FilterChip
              key={chip.key}
              active={filters.author === chip.key}
              count={chip.count}
              onClick={() => setFilters((f) => ({ ...f, author: chip.key }))}
            >
              {chip.label}
            </FilterChip>
          ))}
          <FilterChip
            className="ml-auto"
            active={filters.pinnedOnly}
            count={pinnedCount}
            onClick={() =>
              setFilters((f) => ({ ...f, pinnedOnly: !f.pinnedOnly }))
            }
          >
            <Pin />
            Pinned
          </FilterChip>
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col overflow-auto">
        <div className="mx-auto flex w-full max-w-204 flex-col gap-3 px-6 pt-1 pb-24">
          {error ? <p className="text-sm text-danger">{error}</p> : null}
          <NoteComposer
            authorName={userFullName || "You"}
            avatar={avatarSrc || undefined}
            cases={cases}
            maxChars={maxChars}
            onSubmit={create}
          />
          {showProposals
            ? proposals.map((p) => (
                <NoteProposalCard
                  key={p.approval_id}
                  proposal={p}
                  caseLabel={caseLabelOf(p.case_id)}
                  busy={answering === p.approval_id}
                  onAdd={() => void answer(p, true)}
                  onDismiss={() => void answer(p, false)}
                />
              ))
            : null}
          {narrowed ? (
            <div className="flex items-center gap-2 px-2.5 text-sm text-fg-2">
              <span data-testid="notes-result-count">{narrowedLabel()}</span>
              <Button variant="link" size="xs" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          ) : null}
          {loading ? (
            <div className="flex flex-col gap-4 px-2.5 py-3" aria-busy>
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="size-7 rounded-full" />
                  <div className="flex flex-1 flex-col gap-2">
                    <Skeleton className="h-3.5 w-48" />
                    <Skeleton className="h-3.5 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : notes.length === 0 && !error ? (
            <EmptyState
              icon={StickyNote}
              title="No Notes Yet"
              description="Preferences, sensitivities and decisions about this client. Each note is signed and dated."
            />
          ) : (
            <div className="flex flex-col">
              {pinned.length ? (
                <section className="flex flex-col pb-1.5">
                  <GroupLabel icon label="Pinned" count={pinned.length} />
                  {pinned.map(entry)}
                </section>
              ) : null}
              {months.map((group) => (
                <section
                  key={group.key}
                  className="flex flex-col border-t border-line-1 pt-1.5"
                >
                  <GroupLabel label={group.label} count={group.notes.length} />
                  {group.notes.map(entry)}
                </section>
              ))}
              {narrowed && filtered.length === 0 ? (
                <p className="px-2.5 py-6 text-center text-sm text-fg-3">
                  No notes match.
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
